(() => {
  const money = (cents) => new Intl.NumberFormat('pl-PL', {
    style: 'currency', currency: 'PLN'
  }).format(Number(cents || 0) / 100);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const buildReviewSummary = (reviews, confirmedTotal) => {
    // The curated review list is a subset, not the confirmed store-wide total.
    const confirmedCount = Number(confirmedTotal);
    const rawAverage = reviews.length ? reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviews.length : 0;
    const displayAverage = Math.round(rawAverage);
    return {
      average: displayAverage,
      averageText: String(displayAverage),
      count: Number.isSafeInteger(confirmedCount) && confirmedCount > 0 ? confirmedCount : reviews.length
    };
  };

  function syncReviewSummary(summary) {
    document.querySelectorAll('[data-swp-review-average]').forEach((node) => { node.textContent = summary.averageText; });
    document.querySelectorAll('[data-swp-review-count]').forEach((node) => { node.textContent = String(summary.count); });
    document.querySelectorAll('[data-swp-review-summary-text]').forEach((node) => { node.textContent = `Na podstawie ${summary.count} opinii klientów`; });
    document.querySelectorAll('[data-swp-review-rating-label]').forEach((node) => {
      node.setAttribute('aria-label', `Ocena ${summary.averageText} na 5 na podstawie ${summary.count} opinii`);
      node.querySelectorAll('.swp__rating-star').forEach((star, index) => {
        const filled = index < summary.average;
        star.classList.toggle('swp__rating-star--filled', filled);
        star.classList.toggle('swp__rating-star--neutral', !filled);
      });
    });

    document.querySelectorAll('[data-pb-cart-review]').forEach((cartReview) => {
      const confirmedCount = Number(cartReview.dataset.pbReviewCount);
      const count = Number.isSafeInteger(confirmedCount) && confirmedCount > 0 ? confirmedCount : summary.count;
      cartReview.dataset.pbReviewSource = confirmedCount > 0 ? 'solewise-confirmed-review-count' : 'solewise-curated-reviews';
      cartReview.setAttribute('aria-label', `Ocena ${summary.averageText} na 5 na podstawie ${count} opinii`);
      const output = cartReview.querySelector('[data-pb-cart-review-summary]') || cartReview.querySelector(':scope > span:last-child');
      if (output) output.textContent = `${summary.averageText}/5 (${count} opinii)`;
      cartReview.querySelectorAll('[data-pb-cart-review-star]').forEach((star, index) => {
        const fill = Math.max(0, Math.min(100, (summary.average - index) * 100));
        star.style.setProperty('--pb-cart-review-star-fill', `${fill}%`);
      });
    });

    window.__solewiseReviewSummary = { ...summary, source: 'pb-reviews-pb-39f79c8de318424b.json' };
    document.dispatchEvent(new CustomEvent('solewise:review-summary', { detail: window.__solewiseReviewSummary }));
  }

  function initReveal(root) {
    const items = root.querySelectorAll('[data-swp-reveal]:not([data-swp-reveal-ready])');
    if (!items.length) return;
    if (reducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((item) => { item.dataset.swpVisible = 'true'; item.dataset.swpRevealReady = 'true'; });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.dataset.swpVisible = 'true';
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    items.forEach((item) => { item.dataset.swpRevealReady = 'true'; observer.observe(item); });
  }

  function openDialog(dialog, image, copy, trigger) {
    if (!dialog || !image) return;
    const dialogImage = dialog.querySelector('[data-swp-dialog-image]');
    if (dialogImage) {
      dialogImage.src = image.src || image;
      dialogImage.alt = image.alt || '';
    }
    const dialogCopy = dialog.querySelector('[data-swp-dialog-copy]');
    if (dialogCopy) dialogCopy.innerHTML = copy || '';
    dialog._swpReturnFocus = trigger || document.activeElement;
    dialog.showModal();
    dialog.querySelector('[data-swp-dialog-close]')?.focus();
  }

  function initDialog(dialog) {
    if (!dialog || dialog.dataset.swpReady) return;
    dialog.dataset.swpReady = 'true';
    const close = () => dialog.close();
    dialog.querySelector('[data-swp-dialog-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('close', () => dialog._swpReturnFocus?.focus());
  }

  function initProduct(root) {
    if (root.dataset.swpReady) return;
    root.dataset.swpReady = 'true';
    const reviewDialog = root.querySelector('[data-swp-review-dialog]');
    try {
    const gallerySlides = [...root.querySelectorAll('[data-swp-slide]')];
    const thumbs = [...root.querySelectorAll('[data-swp-thumb]')];
    const dots = [...root.querySelectorAll('[data-swp-dot]')];
    const track = root.querySelector('[data-swp-gallery-track]');
    const zoom = root.querySelector('[data-swp-zoom]');
    const galleryDialog = root.querySelector('[data-swp-gallery-dialog]');
    initDialog(galleryDialog); initDialog(reviewDialog);
    let activeSlide = 0;

    const selectSlide = (index, scrollMobile = false) => {
      activeSlide = Math.max(0, Math.min(gallerySlides.length - 1, index));
      gallerySlides.forEach((slide, i) => slide.setAttribute('aria-hidden', i === activeSlide ? 'false' : 'true'));
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-selected', i === activeSlide ? 'true' : 'false'));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === activeSlide ? 'true' : 'false'));
      if (scrollMobile && matchMedia('(max-width: 820px)').matches) {
        track?.scrollTo({ left: activeSlide * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    };
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => selectSlide(index));
      thumb.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? thumbs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length;
        thumbs[next].focus(); selectSlide(next);
      });
    });
    dots.forEach((dot, index) => dot.addEventListener('click', () => selectSlide(index, true)));
    let galleryTick = false;
    track?.addEventListener('scroll', () => {
      if (galleryTick || !matchMedia('(max-width: 820px)').matches) return;
      galleryTick = true;
      requestAnimationFrame(() => {
        const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        selectSlide(index); galleryTick = false;
      });
    }, { passive: true });
    zoom?.addEventListener('click', () => openDialog(galleryDialog, gallerySlides[activeSlide]?.querySelector('img'), '', zoom));
    } catch (error) {
      root.dataset.swpGalleryError = error?.message || 'gallery_init_failed';
    }

    const form = root.querySelector('.swp__product-form');
    const packageInputs = [...root.querySelectorAll('[data-swp-package]')];
    const mainButton = root.querySelector('[data-swp-main-atc]');
    const stickyButton = root.querySelector('[data-swp-sticky-atc]');
    const price = root.querySelector('[data-swp-price]');
    const stickyPrice = root.querySelector('[data-swp-sticky-price]');
    const stickyOffer = root.querySelector('[data-swp-sticky-offer]');
    const bonus = root.querySelector('[data-swp-bonus]');
    const giftDialog = root.querySelector('[data-swp-gift-dialog]');
    initDialog(giftDialog);
    root.querySelector('[data-swp-gifts-open]')?.addEventListener('click', (event) => {
      if (!giftDialog) return;
      giftDialog._swpReturnFocus = event.currentTarget;
      giftDialog.showModal();
      giftDialog.querySelector('[data-swp-dialog-close]')?.focus();
    });
    const contents = root.querySelector('[data-swp-contents-summary]');
    const status = root.querySelector('[data-swp-form-status]');
    let submitting = false;

    const selected = () => packageInputs.find((input) => input.checked) || packageInputs[0];
    const syncPackage = () => {
      const input = selected(); if (!input) return;
      const cents = Number(input.dataset.price);
      const activeForm = root.querySelector('.swp__product-form');
      const variantInput = activeForm?.querySelector('[name="id"]');
      if (variantInput) variantInput.value = input.value;
      const quantityInput = activeForm?.querySelector('[name="quantity"]');
      if (quantityInput) quantityInput.value = '1';
      const setProperty = (key, value) => {
        const propertyNames = {
          'package-id': '_pb_package_id', 'package-units': '_pb_package_units',
          'package-label': '_pb_package_label', 'bonus-ids': '_pb_bonus_ids',
          'bonus-labels': '_pb_bonus_labels', 'bonus-images': '_pb_bonus_images'
        };
        if (!activeForm || !propertyNames[key]) return;
        const name = `properties[${propertyNames[key]}]`;
        let property = activeForm.elements.namedItem(name);
        if (!property) {
          property = document.createElement('input');
          property.type = 'hidden'; property.name = name;
          activeForm.append(property);
        }
        property.value = value;
      };
      const packageMeta = {
        '48769973059754': { id: 'tier-1', units: '1' },
        '48769973092522': { id: 'tier-2', units: '3' },
        '48769973125290': { id: 'tier-3', units: '4' }
      }[String(input.value)] || { id: '', units: '' };
      setProperty('package-id', packageMeta.id);
      setProperty('package-units', packageMeta.units);
      setProperty('package-label', input.dataset.offer || '');
      const hasGiftPair = input.dataset.bonus === 'true' && Boolean(bonus?.dataset.swpGiftImage);
      setProperty('bonus-ids', input.dataset.bonus === 'true' ? (hasGiftPair ? 'solewise-earplugs-gift,legacy-bonus-1' : 'legacy-bonus-1') : '');
      setProperty('bonus-labels', input.dataset.bonus === 'true' ? (hasGiftPair ? `${bonus.dataset.swpGiftTitle}||` : '') + 'e-book „Czego Twoje Uszy Ci Nie Mówią”' : '');
      setProperty('bonus-images', input.dataset.bonus === 'true' ? (hasGiftPair ? `${bonus.dataset.swpGiftImage}||` : '') + 'https://cdn.shopify.com/s/files/1/0680/7188/2922/files/Okladka_FINAL.png?v=1784119513' : '');
      if (price) price.textContent = money(cents);
      if (mainButton) mainButton.textContent = `Dodaj do koszyka — ${money(cents)}`;
      if (stickyPrice) stickyPrice.textContent = money(cents);
      if (stickyOffer) stickyOffer.textContent = input.dataset.offer;
      if (bonus) bonus.hidden = input.dataset.bonus !== 'true';
      if (contents) contents.textContent = input.dataset.contents;
      if (status) status.textContent = '';
    };
    packageInputs.forEach((input) => input.addEventListener('change', syncPackage));
    form?.addEventListener('submit', (event) => { event.preventDefault(); addSelected(); });
    stickyButton?.addEventListener('click', addSelected);
    root._swpAddSelected = addSelected;
    root._swpSyncPackage = syncPackage;
    try { syncPackage(); } catch (error) { root.dataset.swpCommerceError = error?.message || 'package_sync_failed'; }
    window.addEventListener('pageshow', () => {
      window.requestAnimationFrame(() => { if (root.isConnected) syncPackage(); });
    });

    async function addSelected() {
      const activeForm = root.querySelector('.swp__product-form');
      if (submitting || !activeForm) return;
      syncPackage();
      const chosen = selected();
      if (!chosen) return;
      const variantInput = activeForm.querySelector('[name="id"]');
      if (variantInput) variantInput.value = chosen.value;
      const quantityInput = activeForm.querySelector('[name="quantity"]');
      if (quantityInput) quantityInput.value = '1';
      submitting = true;
      [mainButton, stickyButton].forEach((button) => { if (button) button.disabled = true; });
      if (status) status.textContent = '';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(activeForm)
        });
        const addedItem = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(addedItem.description || 'Nie udało się dodać produktu.');
        let cart = null;
        let cartVerified = false;
        for (let attempt = 0; attempt < 4; attempt += 1) {
          if (attempt) await new Promise((resolve) => window.setTimeout(resolve, attempt * 180));
          const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!cartResponse.ok) continue;
          cart = await cartResponse.json();
          if (cart.items?.some((item) => String(item.variant_id) === String(chosen.value))) { cartVerified = true; break; }
        }
        if (!cart) throw new Error('Nie udało się odświeżyć koszyka.');
        if (!cart.items?.some((item) => String(item.variant_id) === String(chosen.value))) {
          const items = Array.isArray(cart.items) ? cart.items.slice() : [];
          items.push(addedItem);
          cart = { ...cart, items, item_count: items.reduce((sum, item) => sum + Number(item.quantity || 0), 0), total_price: items.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? item.final_price ?? item.price ?? 0), 0) };
        }
        const cartTrigger = document.querySelector('#v-cart-btn[data-pb-action="cart"]');
        const cartController = window.__pageBuilderPremiumCartController;
        const renderedBySource = typeof cartController?.render === 'function';
        if (renderedBySource) cartController.render(cart, { create: true });
        root.dataset.swpCartOwner = cartTrigger && renderedBySource ? 'pagebuilder-source' : 'missing';
        // A verified cart was just fetched; avoid fetching it again while opening.
        if (cartTrigger && renderedBySource && typeof cartController.open === 'function') cartController.open(cartTrigger, { refresh: !cartVerified });
        else if (cartTrigger) cartTrigger.click();
        else if (status) status.textContent = 'Produkt dodano. Otwórz koszyk w nagłówku, aby zobaczyć zawartość.';
      } catch (error) {
        if (status) status.textContent = error.message || 'Nie udało się dodać produktu. Spróbuj ponownie.';
      } finally {
        submitting = false;
        [mainButton, stickyButton].forEach((button) => { if (button) button.disabled = false; });
      }
    }
    const packages = root.querySelector('#swp-packages');
    root.querySelectorAll('[data-swp-to-packages]').forEach((control) => control.addEventListener('click', (event) => {
      event.preventDefault();
      root.querySelector('.swp__product-top')?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => selected()?.focus(), reducedMotion() ? 0 : 450);
    }));
    const reviewRoot = root.querySelector('[data-swp-reviews]');
    let reviewObserver = null;
    const hydrateReviewSection = () => {
      if (!reviewRoot) return;
      reviewObserver?.disconnect();
      reviewObserver = null;
      hydrateReviews(reviewRoot, reviewDialog);
    };
    root.querySelector('[data-swp-to-reviews]')?.addEventListener('click', () => {
      hydrateReviewSection();
      reviewRoot?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth' });
    });

    const sticky = root.querySelector('[data-swp-sticky]');
    const footer = document.querySelector('footer');
    const finalCta = root.querySelector('[data-swp-final-cta]');
    let passedMain = false, footerVisible = false, finalCtaVisible = false;
    const updateSticky = () => {
      const drawerOpen = document.documentElement.classList.contains('cart-open') || document.body.classList.contains('cart-open') || document.querySelector('#cart-drawer.is-open, [data-pb-cart-drawer][data-pb-open="true"], [data-pb-cart-drawer][aria-hidden="false"], [data-cart-drawer].is-open, [data-cart-drawer][aria-hidden="false"]');
      if (sticky) sticky.dataset.swpShow = (passedMain && !footerVisible && !finalCtaVisible && !drawerOpen) ? 'true' : 'false';
    };
    if ('IntersectionObserver' in window && mainButton) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        passedMain = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        updateSticky();
      }, { threshold: .1 });
      observer.observe(mainButton);
      if (footer) new IntersectionObserver((entries) => { footerVisible = entries[0].isIntersecting; updateSticky(); }).observe(footer);
      if (finalCta) new IntersectionObserver((entries) => { finalCtaVisible = entries[0].isIntersecting; updateSticky(); }).observe(finalCta);
      const stickyStateObserver = new MutationObserver(updateSticky);
      stickyStateObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      stickyStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.querySelectorAll('[data-pb-cart-drawer], #cart-drawer, [data-cart-drawer]').forEach((drawer) => {
        stickyStateObserver.observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden', 'data-pb-open'] });
      });
      updateSticky();
    } else if (mainButton) {
      const updateFromPosition = () => { passedMain = mainButton.getBoundingClientRect().bottom < 0; updateSticky(); };
      window.addEventListener('scroll', updateFromPosition, { passive: true });
      updateFromPosition();
    }

    if (reviewRoot) {
      if ('IntersectionObserver' in window) {
        reviewObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) hydrateReviewSection();
        }, { rootMargin: '700px 0px', threshold: 0 });
        reviewObserver.observe(reviewRoot);
      } else {
        hydrateReviewSection();
      }
    }
  }

  async function hydrateReviews(root, dialog) {
    if (root.dataset.swpHydrated) return;
    root.dataset.swpHydrated = 'true';
    const confirmedTotal = root.closest('[data-swp-confirmed-review-count]')?.dataset.swpConfirmedReviewCount;
    const grid = root.querySelector('[data-swp-review-grid]');
    const mediaGrid = root.querySelector('[data-swp-review-media]');
    const more = root.querySelector('[data-swp-review-more]');
    const reviewPageSize = 4;
    let reviews = [], shown = 0;
    const escape = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
    const openReview = (review, media, trigger) => {
      const name = review.author || review.reviewerName || '';
      const verified = review.verified ? '<small>Zweryfikowany zakup</small>' : '';
      openDialog(dialog, { src: media.url, alt: media.alt || `Zdjęcie z opinii${name ? ` — ${name}` : ''}` }, `<div class="swp__dialog-copy"><div class="swp__stars" aria-label="${review.rating} z 5">${'★'.repeat(review.rating)}${'☆'.repeat(5-review.rating)}</div><p>${escape(review.body)}</p>${name ? `<strong>${escape(name)}</strong>` : ''}${verified}</div>`, trigger);
    };
    const card = (review) => {
      const article = document.createElement('article'); article.className = 'swp__review-card';
      article.dataset.swpReviewId = review.id || '';
      const media = review.media?.[0];
      const expandable = String(review.body || '').length > 220;
      article.innerHTML = `<div class="swp__stars" aria-label="${review.rating} z 5">${'★'.repeat(review.rating)}${'☆'.repeat(5-review.rating)}</div><div class="swp__review-body${expandable ? ' swp__review-body--clamped' : ''}" data-swp-review-body data-swp-expanded="false"><p>${escape(review.body)}</p></div>${expandable ? '<button class="swp__review-expand" type="button" aria-expanded="false">Czytaj więcej</button>' : ''}<footer>${review.author ? `<strong>${escape(review.author)}</strong>` : '<span aria-label="Autor niepodany">Autor niepodany</span>'}${review.verified ? '<span>Zweryfikowany zakup</span>' : ''}</footer>${media ? `<button type="button" class="swp__review-photo" aria-label="Powiększ zdjęcie z opinii"><img src="${escape(media.url)}" alt="${escape(media.alt || 'Zdjęcie z opinii klienta SoleWise')}" loading="lazy" width="180" height="180"></button>` : ''}`;
      article.querySelector('.swp__review-expand')?.addEventListener('click', (event) => {
        const expanded = event.currentTarget.getAttribute('aria-expanded') === 'true';
        event.currentTarget.setAttribute('aria-expanded', String(!expanded));
        event.currentTarget.textContent = expanded ? 'Czytaj więcej' : 'Zwiń';
        article.querySelector('[data-swp-review-body]').dataset.swpExpanded = String(!expanded);
      });
      if (media) article.querySelector('.swp__review-photo').addEventListener('click', (event) => openReview(review, media, event.currentTarget));
      return article;
    };
    try {
      const response = await fetch(root.dataset.swpReviewUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Review data unavailable');
      const data = await response.json(); reviews = (data.reviews || []).filter((review) => !review.hidden && review.featuredPlacement !== true);
      syncReviewSummary(buildReviewSummary(reviews, confirmedTotal));
      const mediaItems = reviews.flatMap((review) => (review.media || []).map((media) => ({ review, media }))).slice(0, 5);
      mediaItems.forEach(({ review, media }) => {
        const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-label', 'Powiększ zdjęcie z opinii');
        button.innerHTML = `<img src="${escape(media.url)}" alt="${escape(media.alt || 'Zdjęcie z opinii klienta SoleWise')}" loading="lazy" width="500" height="570">`;
        button.addEventListener('click', () => openReview(review, media, button)); mediaGrid?.append(button);
      });
      const render = () => {
        reviews.slice(shown, shown + reviewPageSize).forEach((review) => grid?.append(card(review)));
        shown = Math.min(reviews.length, shown + reviewPageSize);
        const remaining = Math.max(0, reviews.length - shown);
        if (more) { more.hidden = remaining === 0; more.textContent = `Pokaż więcej opinii (${remaining})`; }
      };
      more?.addEventListener('click', render); render();
    } catch (_) {
      if (grid) grid.innerHTML = '<p>Opinie są chwilowo niedostępne. Spróbuj ponownie później.</p>';
      if (more) more.hidden = true;
    }
  }

  function initSocialProof(root) {
    if (root.dataset.swpSocialProofReady) return;
    const track = root.querySelector('[data-swp-social-proof-track]');
    const cards = [...root.querySelectorAll('[data-swp-social-proof-card]')];
    const previous = root.querySelector('[data-swp-social-proof-prev]');
    const next = root.querySelector('[data-swp-social-proof-next]');
    const status = root.querySelector('[data-swp-social-proof-status]');
    const progress = [...root.querySelectorAll('[data-swp-social-proof-progress] > span')];
    if (!track || cards.length !== 5 || !previous || !next || !status || progress.length !== cards.length) return;
    root.dataset.swpSocialProofReady = 'true';
    const autoAdvanceInterval = 6500;
    const manualPauseInterval = 10000;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = -1;
    let scrollFrame = 0;
    let autoTimer = 0;
    let programmaticTimer = 0;
    let manualPauseUntil = 0;
    let sectionVisible = false;
    let pointerInside = false;
    let focusInside = false;
    let programmaticScroll = false;

    const update = (index) => {
      const boundedIndex = Math.max(0, Math.min(cards.length - 1, index));
      if (boundedIndex === activeIndex) return;
      activeIndex = boundedIndex;
      cards.forEach((card, cardIndex) => {
        const active = cardIndex === activeIndex;
        card.dataset.swpActive = String(active);
        if (active) card.setAttribute('aria-current', 'true'); else card.removeAttribute('aria-current');
      });
      progress.forEach((segment, segmentIndex) => { segment.dataset.swpActive = String(segmentIndex === activeIndex); });
      previous.disabled = activeIndex === 0;
      next.disabled = activeIndex === cards.length - 1;
      status.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
    };

    const clearAutoTimer = () => {
      if (!autoTimer) return;
      window.clearTimeout(autoTimer);
      autoTimer = 0;
    };

    const canAutoAdvance = () => (
      !motionQuery.matches
      && sectionVisible
      && !document.hidden
      && !pointerInside
      && !focusInside
      && Date.now() >= manualPauseUntil
    );

    const scheduleAutoAdvance = () => {
      clearAutoTimer();
      if (motionQuery.matches) return;
      const pauseRemaining = manualPauseUntil - Date.now();
      if (pauseRemaining > 0) {
        autoTimer = window.setTimeout(scheduleAutoAdvance, pauseRemaining + 50);
        return;
      }
      if (!canAutoAdvance()) return;
      autoTimer = window.setTimeout(() => {
        autoTimer = 0;
        if (!canAutoAdvance()) {
          scheduleAutoAdvance();
          return;
        }
        goTo((activeIndex + 1) % cards.length);
        scheduleAutoAdvance();
      }, autoAdvanceInterval);
    };

    const settleProgrammaticScroll = () => {
      if (!programmaticScroll) return;
      programmaticScroll = false;
      window.clearTimeout(programmaticTimer);
      programmaticTimer = 0;
      updateFromScroll();
    };

    const goTo = (index) => {
      const nextIndex = Math.max(0, Math.min(cards.length - 1, index));
      update(nextIndex);
      programmaticScroll = true;
      window.clearTimeout(programmaticTimer);
      programmaticTimer = window.setTimeout(settleProgrammaticScroll, reducedMotion() ? 40 : 900);
      track.scrollTo({ left: cards[nextIndex].offsetLeft - cards[0].offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
    };

    const updateFromScroll = () => {
      scrollFrame = 0;
      if (programmaticScroll) return;
      const maximumScroll = track.scrollWidth - track.clientWidth;
      let closestIndex;
      if (track.scrollLeft <= 2) closestIndex = 0;
      else if (maximumScroll - track.scrollLeft <= 2) closestIndex = cards.length - 1;
      else {
        const trackLeft = track.getBoundingClientRect().left;
        closestIndex = cards.reduce((closest, card, index) => (
          Math.abs(card.getBoundingClientRect().left - trackLeft) < Math.abs(cards[closest].getBoundingClientRect().left - trackLeft) ? index : closest
        ), 0);
      }
      if (closestIndex !== activeIndex) update(closestIndex);
    };

    const pauseAfterInteraction = () => {
      manualPauseUntil = Date.now() + manualPauseInterval;
      programmaticScroll = false;
      window.clearTimeout(programmaticTimer);
      programmaticTimer = 0;
      scheduleAutoAdvance();
    };

    const handlePrevious = () => { pauseAfterInteraction(); goTo(activeIndex - 1); };
    const handleNext = () => { pauseAfterInteraction(); goTo(activeIndex + 1); };
    const handleScroll = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(updateFromScroll);
    };
    const handleKeydown = (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      pauseAfterInteraction();
      if (event.key === 'Home') goTo(0);
      else if (event.key === 'End') goTo(cards.length - 1);
      else goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    };
    const handlePointerEnter = () => { pointerInside = true; scheduleAutoAdvance(); };
    const handlePointerLeave = () => { pointerInside = false; scheduleAutoAdvance(); };
    const handleFocusIn = () => { focusInside = true; scheduleAutoAdvance(); };
    const handleFocusOut = () => window.setTimeout(() => {
      focusInside = root.contains(document.activeElement);
      scheduleAutoAdvance();
    }, 0);
    const handleVisibilityChange = () => scheduleAutoAdvance();
    const handleMotionChange = () => scheduleAutoAdvance();
    const handleWheel = (event) => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY) * .35) pauseAfterInteraction(); };

    previous.addEventListener('click', handlePrevious);
    next.addEventListener('click', handleNext);
    track.addEventListener('scroll', handleScroll, { passive: true });
    track.addEventListener('scrollend', settleProgrammaticScroll);
    track.addEventListener('keydown', handleKeydown);
    track.addEventListener('pointerdown', pauseAfterInteraction, { passive: true });
    track.addEventListener('wheel', handleWheel, { passive: true });
    root.addEventListener('pointerenter', handlePointerEnter);
    root.addEventListener('pointerleave', handlePointerLeave);
    root.addEventListener('focusin', handleFocusIn);
    root.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    motionQuery.addEventListener?.('change', handleMotionChange);

    let visibilityObserver;
    let fallbackVisibilityFrame = 0;
    const measureVisibility = () => {
      fallbackVisibilityFrame = 0;
      const bounds = root.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(bounds.bottom, window.innerHeight) - Math.max(bounds.top, 0));
      sectionVisible = bounds.height > 0 && visibleHeight / bounds.height >= .6;
      scheduleAutoAdvance();
    };
    const requestVisibilityMeasure = () => {
      if (fallbackVisibilityFrame) return;
      fallbackVisibilityFrame = requestAnimationFrame(measureVisibility);
    };
    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver((entries) => {
        sectionVisible = entries[0].intersectionRatio >= .6;
        scheduleAutoAdvance();
      }, { threshold: [.6] });
      visibilityObserver.observe(root);
    } else {
      window.addEventListener('scroll', requestVisibilityMeasure, { passive: true });
      window.addEventListener('resize', requestVisibilityMeasure);
      measureVisibility();
    }

    const productRoot = root.closest('[data-swp-product]') || root.closest('.swp');
    const sticky = productRoot?.querySelector('[data-swp-sticky]');
    let stickyResizeObserver;
    let stickyMutationObserver;
    const measureStickySafeArea = () => {
      if (!sticky || !window.matchMedia('(max-width: 820px)').matches) {
        root.style.setProperty('--swp-social-proof-sticky-safe', '0px');
        return;
      }
      const visible = sticky.dataset.swpShow === 'true' && getComputedStyle(sticky).display !== 'none';
      const height = visible ? Math.ceil(sticky.getBoundingClientRect().height) : 0;
      root.style.setProperty('--swp-social-proof-sticky-safe', `${height}px`);
    };
    if (sticky) {
      stickyResizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measureStickySafeArea) : null;
      stickyResizeObserver?.observe(sticky);
      stickyMutationObserver = new MutationObserver(measureStickySafeArea);
      stickyMutationObserver.observe(sticky, { attributes: true, attributeFilter: ['data-swp-show', 'style', 'class'] });
    }
    window.addEventListener('resize', measureStickySafeArea);
    measureStickySafeArea();

    root._swpSocialProofCleanup = () => {
      clearAutoTimer();
      window.clearTimeout(programmaticTimer);
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      if (fallbackVisibilityFrame) cancelAnimationFrame(fallbackVisibilityFrame);
      previous.removeEventListener('click', handlePrevious);
      next.removeEventListener('click', handleNext);
      track.removeEventListener('scroll', handleScroll);
      track.removeEventListener('scrollend', settleProgrammaticScroll);
      track.removeEventListener('keydown', handleKeydown);
      track.removeEventListener('pointerdown', pauseAfterInteraction);
      track.removeEventListener('wheel', handleWheel);
      root.removeEventListener('pointerenter', handlePointerEnter);
      root.removeEventListener('pointerleave', handlePointerLeave);
      root.removeEventListener('focusin', handleFocusIn);
      root.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      motionQuery.removeEventListener?.('change', handleMotionChange);
      visibilityObserver?.disconnect();
      window.removeEventListener('scroll', requestVisibilityMeasure);
      window.removeEventListener('resize', requestVisibilityMeasure);
      window.removeEventListener('resize', measureStickySafeArea);
      stickyResizeObserver?.disconnect();
      stickyMutationObserver?.disconnect();
      delete root._swpSocialProofCleanup;
      delete root.dataset.swpSocialProofReady;
    };
    update(0);
    scheduleAutoAdvance();
  }

  function init(scope = document) {
    scope.querySelectorAll('.swp').forEach(initReveal);
    scope.querySelectorAll('[data-swp-product]').forEach(initProduct);
    scope.querySelectorAll('[data-swp-social-proof]').forEach(initSocialProof);
    scope.querySelectorAll('[data-swp-home][data-swp-review-url]:not([data-swp-review-ready])').forEach(async (root) => {
      root.dataset.swpReviewReady = 'true';
      try {
        const response = await fetch(root.dataset.swpReviewUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        const reviews = (data.reviews || []).filter((review) => !review.hidden && review.featuredPlacement !== true);
        syncReviewSummary(buildReviewSummary(reviews, root.dataset.swpConfirmedReviewCount));
      } catch (_) {}
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-swp-social-proof]').forEach((root) => root._swpSocialProofCleanup?.()));
})();
